<script setup>
import Layout from "../Layouts/Customer.vue";
import Button from "primevue/button";
import MultiSelect from "primevue/multiselect";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import {useForm} from "@inertiajs/inertia-vue3";
import {computed, ref, onMounted} from "vue";
import Card from "primevue/card";
import Tag from "primevue/tag";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import {useVuelidate} from "@vuelidate/core";
import {required$} from "../utils/validators";
import {scrollToFirstError} from "../utils/form";
import DeviceReasonsOP from "../Components/DeviceReasonsOP.vue";
import {Link} from "@inertiajs/inertia-vue3";
import SimplybookIcon from "../Components/SimplybookIcon.vue";


const props = defineProps([
    'customer',
    'menuItems',
    'flash',
    'packageOptions',
    'customerPackageStatusDataMap',
    'deviceReasonsMap',
    'devices',
    'max2HoursOptions',
    'deviceData',
    'totalPackageBookings',
]);

const toast = useToast();
const confirm = useConfirm()
const submitting = ref(false)
const isVisibleAddPackage = ref(false)
const deviceReasonsOP = ref(null)

const form = useForm({
    package_id: null,
    customer_id: props.customer.id,
});


const rules = {
    form: {
        package_id: {
            required$,
        },
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const maxUnits = computed(() => {
    let value = 0

    props.customer.customer_packages.forEach((data) => {
        value += data.package.limit
    })

    return value
})

const addPackage = async () => {
    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()

        return
    }

    submitting.value = true

    form.post('/customer-packages', {
        preserveState: false,

        onSuccess: (page) => {
            toast.add({
                severity: "success",
                summary: "Aktualisiert",
                life: 5000,
            });
        },

        onFinish: (visit) => {
            submitting.value = false;
        }
    })
}

const deletePackage = async (name, uuid) => {
    confirm.require({
        message: `Sind Sie sicher, dass Sie ${name} löschen wollen?`,
        header: 'Konfirmation',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Abbrechen',
        acceptLabel: 'Löschen',
        accept: () => {
            toast.add({
                severity: "info",
                summary: "Löschen von...",
                life: 5000,
            });

            useForm().delete(`/customer-packages/${uuid}`, {
                preserveState: false,

                onSuccess: (page) => {
                    toast.add({
                        severity: "success",
                        summary: "Aktualisiert",
                        life: 5000,
                    });
                },
            })
        },
        reject: () => {
        }
    });
}

const togglePopOver = (e, device) => {
    if (device.pivot?.warning_type != 'maybe') {
        return
    }

    deviceReasonsOP.value.togglePopOver(e, device)
}

onMounted(() => {
    if (props.customer?.is_measurement_stale) {
        toast.add({
            severity: 'error',
            summary: 'Messung veraltet',
            detail: 'Die Messungen dieses Kunden wurden seit über 3 Wochen nicht aktualisiert.',
            life: 0,
            closable: true
        });
    }
});

</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="py-7 text-sm">
            <div class="px-14 mb-7">
                <div class="flex justify-end mb-7">
                    <Button
                        label="Pauschalangebot hinzufügen"
                        severity="success"
                        icon="pi pi-plus"
                        class="text-sm"
                        @click="isVisibleAddPackage = true" />
                </div>

                <div v-if="customer.customer_packages.length" class="grid grid-cols-3 gap-7">
                    <Card v-for="(item, idx) in customer.customer_packages"
                          :key="`customer_packages_${idx}`">
                        <template #title>{{ item.package.name }}</template>
                        <template #content>
                            <div class="mb-2">
                                <div class="flex gap-3"
                                     @mouseenter="togglePopOver($event, item.package.device)">
                                    <i class="pi pi-exclamation-circle" :class="{
                                        'text-[#FFFFFF]': item.package.device?.pivot?.warning_type == null,
                                        'text-[#FFC000]': item.package.device?.pivot?.warning_type == 'maybe'
                                    }"></i>

                                    <span v-if="item.package.device?.pivot?.warning_type == 'maybe'"
                                          class="text-xs font-extralight">Freigabe durch VIGORY nötig</span>
                                </div>
                            </div>

                            <div>
                                <Tag :severity="customerPackageStatusDataMap[item.status].severity"
                                     :value="customerPackageStatusDataMap[item.status].label"></Tag>
                            </div>
                        </template>
                        <template #footer>
                            <div class="flex justify-end">
                                <i
                                    class="pi pi-trash text-[#FF0000] cursor-pointer"
                                    @click="deletePackage(item.package.name, item.uuid)"></i>
                            </div>
                        </template>
                    </Card>
                </div>
                <div v-else>
                    Sie haben keine Pauschalangebote
                </div>
            </div>

            <!-- Device Count Table -->
            <div class="px-14">
                <div v-if="deviceData && deviceData.length > 0" class="mt-9">
                    <div class="bg-[#EEEBE4] rounded-t-lg">
                        <div class="px-14 py-4">
                            <h3 class="text-lg font-bold">Geräte</h3>
                        </div>
                    </div>
                    
                    <div class="bg-white rounded-b-lg shadow-sm">
                        <div class="px-14 py-4">
                            <!-- Table Header -->
                            <div class="grid grid-cols-6 gap-0 items-center mb-2">
                                <div class="col-span-4 text-sm font-bold">Gerät</div>
                                <div class="col-span-2 text-sm font-bold text-center">Buchungen</div>
                            </div>
                            
                            <!-- Device Rows -->
                            <div v-for="device in deviceData" :key="device.id" 
                                 class="grid grid-cols-6 gap-0 items-center">
                                <div class="col-span-4 border-b border-[#C5C6CC] py-3">
                                    <span class="text-sm">{{ device.name }}</span>
                                </div>
                                <div class="col-span-2 border-b border-[#C5C6CC] py-3 text-center flex items-center justify-center gap-2">
                                    <div>
                                        <Link :href="`/bookings/${customer.uuid}/${device.uuid}?source=packages`"
                                              class="text-black hover:text-blue-600 underline text-sm font-bold">
                                            {{ device.bookingsCount }}
                                        </Link>
                                        <span class="text-sm"> / {{ device.totalLimit }}</span>
                                    </div>
                                    <!-- SimplyBook Bookings Link -->
                                    <Link 
                                        :href="`/customers/${customer.uuid}/simplybook-bookings/${device.uuid}`"
                                        class="text-blue-600 hover:text-blue-700"
                                        title="SimplyBook Buchungen anzeigen"
                                    >
                                        <SimplybookIcon :size="14" />
                                    </Link>
                                </div>
                            </div>
                            
                            <!-- Total -->
                            <div class="mt-4">
                                <h4 class="text-sm font-bold">Total Einheiten: {{ totalPackageBookings || 0 }}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Show message when no active packages with devices -->
                <div v-else-if="customer.customer_packages.length === 0" class="mt-9 text-center text-gray-500">
                    Sie haben keine aktiven Pakete
                </div>
            </div>
        </div>

        <Dialog v-model:visible="isVisibleAddPackage"
                header="Pauschalangebot hinzufügen"
                :style="{ width: '25rem' }"
                modal>
            <div class="mb-7">
                <Dropdown
                    :invalid="v$.form.package_id.$error"
                    v-model="form.package_id"
                    :options="packageOptions"
                    option-label="label"
                    option-value="value"
                    class="text-sm w-full"
                    @change="v$.form.package_id.$touch"/>

                <div v-if="v$.form.package_id.$error" class="text-sm text-[#dc2626]">
                    {{ v$.form.package_id.$errors[0].$message }}
                </div>
            </div>

            <div class="flex justify-end gap-2">
                <Button
                    type="button"
                    label="Abbrechen"
                    severity="secondary"
                    class="text-sm"
                    @click="isVisibleAddPackage = false" />

                <Button
                    :loading="submitting"
                    type="button"
                    label="Hinzufügen"
                    class="text-sm"
                    @click="addPackage"></Button>
            </div>
        </Dialog>

        <DeviceReasonsOP ref="deviceReasonsOP"
                         :device-reasons-map="deviceReasonsMap"></DeviceReasonsOP>
    </Layout>
</template>
